import { type FieldArrayPath, type FieldValues, type Path, type UseFieldArrayProps, useFieldArray, useFormContext } from 'react-hook-form';

/**
 * A field array whose list-level message, such as "add at least 1", goes once a row is added or removed, and comes back on the next
 * Continue if it still applies. react-hook-form only re-checks a list itself for errors with a type, which the standard-schema resolver
 * leaves empty.
 */
export const useFormFieldArray = <T extends FieldValues, Name extends FieldArrayPath<T>>(props: UseFieldArrayProps<T, Name>) => {
  const form = useFormContext<T>();
  const fieldArray = useFieldArray(props);
  const clearListError = () => {
    const { error } = form.getFieldState(props.name as Path<T>);

    // An empty list carries its message itself, and a list with rows on its root
    if (error?.root) form.clearErrors(`${props.name}.root` as Path<T>);
    else if (error?.message) form.clearErrors(props.name as Path<T>);
  };

  return {
    ...fieldArray,
    append: (...args: Parameters<typeof fieldArray.append>) => {
      fieldArray.append(...args);
      clearListError();
    },
    remove: (...args: Parameters<typeof fieldArray.remove>) => {
      fieldArray.remove(...args);
      clearListError();
    },
  };
};
